// creditNotePdf.js (P4-CC10) — branded Credit Note PDF (jsPDF + jspdf-autotable).
// Consumes the getCreditNote result + Business Settings; mirrors CreditNoteView + the
// invoice style (branded header, linked invoice ref, per-band VAT totals, bank details).
// ALL dynamic text (doc.text args + autotable cells) is routed through asciiPdf(A) —
// jsPDF's WinAnsi Helvetica can't render en/em dashes, arrows, smart quotes, etc.
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { COLORS, money, businessBrand, brandedHeader, brandedFooter, asciiPdf as A } from "./pdfBrand";
import { CREDIT_REASONS } from "./creditNotes";   // read-only constant import

const reasonLabel = (v) => CREDIT_REASONS.find((r) => r.value === v)?.label || (v || "-");

/** Build (but don't save) the jsPDF credit-note document. */
export function buildCreditNoteDoc(creditNote, settings) {
  const cn = creditNote || {};
  const B = businessBrand(settings);
  const lines = (cn.credit_note_items || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  const bands = cn.vat_breakdown || [];
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 14, rightX = pageW - M;

  let y = brandedHeader(doc, { title: "CREDIT NOTE", docNumber: cn.credit_note_number || "-", business: B });

  // FROM (left)
  doc.setTextColor(...COLORS.GREY); doc.setFontSize(8); doc.setFont("helvetica", "bold");
  doc.text(A("FROM"), M, y);
  doc.setFont("helvetica", "normal"); doc.setTextColor(...COLORS.NAVY); doc.setFontSize(9);
  let by = y + 5;
  [B.name, ...B.addressLines, B.email, B.vat ? `VAT: ${B.vat}` : null].filter(Boolean).forEach((line) => { doc.text(A(line), M, by); by += 4.5; });

  // Meta (right)
  const metaX = pageW - M - 70;
  doc.setFillColor(...COLORS.LIGHT); doc.roundedRect(metaX, y - 4, 70, 24, 2, 2, "F");
  const metaRow = (label, value, row) => {
    const ry = y + 1 + row * 6;
    doc.setFont("helvetica", "bold"); doc.setTextColor(...COLORS.GREY); doc.setFontSize(7.5);
    doc.text(A(label), metaX + 3, ry);
    doc.setFont("helvetica", "normal"); doc.setTextColor(...COLORS.NAVY); doc.setFontSize(9);
    doc.text(A(value || "-"), rightX - 3, ry, { align: "right" });
  };
  metaRow("ISSUE DATE", cn.issue_date, 0);
  metaRow("AGAINST INVOICE", cn.invoices?.invoice_number, 1);
  metaRow("REASON", reasonLabel(cn.reason), 2);

  y = Math.max(by, y + 24) + 4;

  // CREDIT TO (customer)
  doc.setFillColor(...COLORS.LIGHT); doc.roundedRect(M, y, pageW - 2 * M, 18, 2, 2, "F");
  doc.setFont("helvetica", "bold"); doc.setTextColor(...COLORS.GREY); doc.setFontSize(7.5);
  doc.text(A("CREDIT TO"), M + 4, y + 6);
  doc.setFont("helvetica", "bold"); doc.setTextColor(...COLORS.NAVY); doc.setFontSize(11);
  doc.text(A(cn.customer_name || "-"), M + 4, y + 12);
  y += 24;

  // Line items
  const rows = lines.map((l) => [
    l.description || "", l.sku || "-", String(l.quantity ?? 0), money(l.unit_price),
    `${l.discount_percent ?? 0}%`, `${l.vat_rate ?? 0}%`, l.restock ? "Yes" : (l.product_id ? "No" : "-"), money(l.line_total),
  ]);
  const body = (rows.length ? rows : [["No lines", "", "", "", "", "", "", ""]]).map((r) => r.map(A));
  autoTable(doc, {
    startY: y,
    head: [["Description", "SKU", "Qty", "Unit Price", "Disc %", "VAT %", "Restock", "Line Total"].map(A)],
    body,
    margin: { left: M, right: M },
    styles: { fontSize: 8.5, cellPadding: 2.5, lineColor: [226, 232, 240], lineWidth: 0.1, textColor: COLORS.NAVY },
    headStyles: { fillColor: COLORS.NAVY, textColor: 255, fontStyle: "bold", fontSize: 8 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 0: { cellWidth: "auto" }, 1: { cellWidth: 20 }, 2: { cellWidth: 12, halign: "right" }, 3: { cellWidth: 22, halign: "right" }, 4: { cellWidth: 16, halign: "right" }, 5: { cellWidth: 14, halign: "right" }, 6: { cellWidth: 16, halign: "center" }, 7: { cellWidth: 24, halign: "right" } },
  });
  y = doc.lastAutoTable.finalY + 6;

  const ensureSpace = (needed) => { if (y + needed > pageH - 14) { doc.addPage(); y = 18; } };
  ensureSpace(60);

  // Totals (right)
  const totX = pageW - M - 80;
  const totRow = (label, value, opts = {}) => {
    doc.setFontSize(9); doc.setFont("helvetica", opts.bold ? "bold" : "normal"); doc.setTextColor(...(opts.muted ? COLORS.GREY : COLORS.NAVY));
    doc.text(A(label), totX, y); doc.text(A(value), rightX, y, { align: "right" }); y += 5.5;
  };
  totRow("Subtotal (excl. VAT)", money(cn.subtotal));
  if (cn.total_discount > 0) totRow("Discount", `-${money(cn.total_discount)}`, { muted: true });
  bands.forEach((b) => totRow(`VAT @ ${b.rate}%`, money(b.vat), { muted: true }));
  totRow("Total VAT", money(cn.total_vat), { bold: true });
  if (cn.total_drs > 0) totRow("DRS Deposit", money(cn.total_drs), { bold: true });

  // Credit total bar
  y += 1;
  doc.setFillColor(...COLORS.BLUE); doc.roundedRect(totX - 3, y - 4.5, 83, 9, 1.5, 1.5, "F");
  doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text(A("CREDIT TOTAL"), totX, y + 1.5);
  doc.text(A(money(cn.total_amount)), rightX - 1, y + 1.5, { align: "right" });
  y += 14;

  // Bank details (mirror invoice)
  if (B.iban || B.bank_name) {
    ensureSpace(26);
    doc.setFillColor(236, 245, 255); doc.roundedRect(M, y, pageW - 2 * M, 22, 2, 2, "F");
    doc.setFont("helvetica", "bold"); doc.setTextColor(...COLORS.BLUE); doc.setFontSize(8);
    doc.text(A("BANK DETAILS"), M + 4, y + 6);
    doc.setFont("helvetica", "normal"); doc.setTextColor(...COLORS.NAVY); doc.setFontSize(8.5);
    // SEND-0b: print the line only when there is something to print. This read
    // `${B.bank_account_name || B.name}`, and once businessBrand stopped
    // defaulting the name to "Msquared" a tenant with neither field set would
    // have rendered the label "Account Name:" followed by nothing — the same
    // dangling-label defect SEND-0 fixed on the invoice. The surrounding block
    // stays conditional on `B.iban || B.bank_name`, which is already correct.
    const accountName = B.bank_account_name || B.name;
    if (accountName) doc.text(A(`Account Name:  ${accountName}`), M + 4, y + 12);
    if (B.bank_name) doc.text(A(`Bank:  ${B.bank_name}`), M + 4, y + 16.5);
    if (B.iban) doc.text(A(`IBAN:  ${B.iban}`), pageW / 2 + 10, y + 12);
    if (B.bic) doc.text(A(`BIC:  ${B.bic}`), pageW / 2 + 10, y + 16.5);
    y += 28;
  }

  // Notes
  if (cn.notes) {
    ensureSpace(14);
    doc.setFont("helvetica", "italic"); doc.setTextColor(...COLORS.GREY); doc.setFontSize(8.5);
    const noteLines = doc.splitTextToSize(A(`Notes: ${cn.notes}`), pageW - 2 * M);
    doc.text(noteLines, M, y); y += noteLines.length * 4.5;
  }

  brandedFooter(doc, B);
  return doc;
}

/** Build + trigger a browser download. */
export function downloadCreditNotePDF(creditNote, settings) {
  const doc = buildCreditNoteDoc(creditNote, settings);
  const num = (creditNote?.credit_note_number || "credit-note").replace(/[^\w-]+/g, "_");
  doc.save(`CreditNote-${num}.pdf`);
}
